import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Plus, XCircle, ChevronUp } from 'lucide-react'
import useAuthStore from '../../stores/authStore'
import useEditStore from '../../controllers/EditController'
import ConfirmationModal from '../shared/ConfirmationModal'
import Skeleton from '../shared/Skeleton'
import generationsApi from '../../services/generationsApi'
import { imageLoadingQueue } from '../../utils/imageLoadingQueue'
import { getThumbnailFromCache, addThumbnailToCache } from '../../utils/thumbnailCache'
import { Spinner } from '../ui/spinner'

const FilmstripItem = ({ 
  group, 
  selectImage, 
  isAddButton, 
  onAddClick, 
  isSelected,
}) => {
  const token = useAuthStore(state => state.token)
  const [thumbnailUrl, setThumbnailUrl] = useState(null)
  const [elapsedTime, setElapsedTime] = useState(0)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [isLoadingThumbnail, setIsLoadingThumbnail] = useState(false)
  const timerRef = useRef(null)
  const [showVersions, setShowVersions] = useState(false)
  const isBrushing = useEditStore(state => state.isBrushing)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const pendingImage = useRef(null)
  const [isVisible, setIsVisible] = useState(false)
  const itemRef = useRef(null)

  const getLatestVersion = useCallback(() => {
    if (!group?.versions?.length) return null
    
    // During processing, use the processing version
    const processingVersion = group.versions.find(v => v.status === 'processing')
    if (processingVersion) return processingVersion

    // After processing, use the completed version
    const completedVersion = group.versions.find(v => v.status === 'completed')
    if (completedVersion) return completedVersion

    // Fallback to latest version
    return group.versions[group.versions.length - 1]
  }, [group?.versions])

  const latestVersion = useMemo(() => getLatestVersion(), [group?.versions])
  const isProcessing = group?.versions?.some(v => v.status === 'processing')
  const hasFailed = group?.versions?.some(v => v.status === 'failed')

  useEffect(() => {
    if (isProcessing) {
      // Get the processing version's startedAt time
      const processingVersion = group?.versions?.find(v => v.status === 'processing')
      const startTime = processingVersion?.startedAt ? new Date(processingVersion.startedAt).getTime() : Date.now()
      
      timerRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000))
      }, 1000)
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [isProcessing, group?.versions])

  useEffect(() => {
    if (!itemRef.current || isAddButton) return

    const observerCallback = (entries) => {
      const [entry] = entries
      const isNowVisible = entry.isIntersecting
      
      if (isNowVisible !== isVisible) {
        setIsVisible(isNowVisible)
        console.debug('[FilmstripItem] Visibility changed:', {
          groupId: group?.groupId,
          isVisible: isNowVisible
        })
      }
    }

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
      rootMargin: '100px'
    })

    observer.observe(itemRef.current)

    return () => observer.disconnect()
  }, [group?.groupId, isAddButton, isVisible])

  useEffect(() => {
    const currentVersion = getLatestVersion()
    const wasProcessing = currentVersion?.status === 'processing'
    
    console.debug('[FilmstripItem] Version status monitoring:', {
      groupId: group?.groupId,
      versionId: currentVersion?.id,
      status: currentVersion?.status,
      wasProcessing,
      hasUrl: !!thumbnailUrl
    })

    if (currentVersion?.status === 'completed') {
      console.debug('[FilmstripItem] Version completed, initiating thumbnail load', {
        groupId: group?.groupId,
        versionId: currentVersion.id
      })
      
      // Clear existing thumbnail and loading states
      // setThumbnailUrl(null)
      setImageLoaded(false)
      setIsLoadingThumbnail(true)
      
      // Force immediate reload of the image
      imageLoadingQueue.removeFromQueues(currentVersion.id)
      imageLoadingQueue.addToVisibleQueue(currentVersion.id, true)
    }
  }, [group?.versions, getLatestVersion])

  useEffect(() => {
    let mounted = true
    const currentVersion = getLatestVersion()
    
    if (!currentVersion?.id || !isVisible || currentVersion.status !== 'completed') {
      return
    }

    const imageId = currentVersion.id
    
    // Check cache first
    const cachedUrl = getThumbnailFromCache(imageId)
    if (cachedUrl) {
      console.debug('[FilmstripItem] Using cached thumbnail', { imageId })
      setThumbnailUrl(cachedUrl)
      setIsLoadingThumbnail(false)
      setImageLoaded(true)
      return
    }

    // Set loading state if not cached
    if (!thumbnailUrl) {
      setIsLoadingThumbnail(true)
    }

    const handleImageLoaded = (event) => {
      if (!mounted) return
      
      const { success, url, error } = event.detail
      
      if (success && url) {
        addThumbnailToCache(imageId, url)
        setThumbnailUrl(url)
        setIsLoadingThumbnail(false)
        setImageLoaded(true)
      } else {
        console.error('[FilmstripItem] Failed to load thumbnail:', error)
        setIsLoadingThumbnail(false)
        
        // Retry loading after a delay
        setTimeout(() => {
          if (mounted && isVisible) {
            imageLoadingQueue.addToVisibleQueue(imageId, true)
          }
        }, 2000)
      }
    }

    window.addEventListener(`imageLoaded:${imageId}`, handleImageLoaded)
    
    // Only add to queue if visible and not cached
    if (isVisible && !thumbnailUrl && !getThumbnailFromCache(imageId)) {
      console.debug('[FilmstripItem] Adding to visible queue', { imageId })
      imageLoadingQueue.addToVisibleQueue(imageId)
    }

    return () => {
      mounted = false
      window.removeEventListener(`imageLoaded:${imageId}`, handleImageLoaded)
    }
  }, [group?.versions, isVisible, thumbnailUrl, getLatestVersion])

  const handleImageSelect = (selectedGroup) => {
    if (isBrushing) {
      pendingImage.current = selectedGroup
      setShowConfirmModal(true)
      return
    }

    // Find the latest completed version
    const latestCompletedVersion = [...selectedGroup.versions]
      .sort((a, b) => b.versionNumber - a.versionNumber)
      .find(v => v.status === 'completed')

    if (latestCompletedVersion) {
      selectImage({
        ...selectedGroup,
        selectedVersion: latestCompletedVersion,
        src: `${process.env.REACT_APP_API_URL}/generations/image/${latestCompletedVersion.id}`
      })
    }
  }

  return (
    <div 
      ref={itemRef}
      data-image-id={group?.originalGenerationId}
      className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden mr-2
        ${isSelected ? 'ring-2 ring-blue-500' : 'ring-1 ring-gray-700'}
        ${isProcessing ? 'animate-pulse' : ''}
        ${hasFailed ? 'ring-red-500' : ''}`}
    >
      {isAddButton ? (
        <button
          onClick={isBrushing ? () => setShowConfirmModal(true) : onAddClick}
          className={`w-full h-full bg-gray-800 rounded-lg flex items-center justify-center 
            ${isBrushing ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-700'} transition-colors`}
          aria-label="Add new image"
          disabled={isBrushing}
        >
          <Plus className="text-gray-400" />
        </button>
      ) : (
        <>
          <button
            onClick={() => !isProcessing && handleImageSelect(group)}
            className={`relative w-full h-full rounded-lg overflow-hidden transition-all
              ${isSelected ? 'ring-2 ring-blue-500' : 'hover:ring-2 hover:ring-gray-500'}
              ${isProcessing || hasFailed ? 'bg-gray-800' : ''}
              ${isBrushing && !isSelected ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-image-id={group?.groupId}
            disabled={isBrushing && !isSelected}
          >
            {isProcessing ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/75">
                <Spinner className="w-5 h-5 text-blue-500" />
                <div className="text-xs text-gray-300 mt-2 flex flex-col items-center">
                  <span className="text-gray-400">{elapsedTime}s</span>
                </div>
              </div>
            ) : hasFailed ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/75">
                <XCircle className="w-5 h-5 text-red-500" />
                <span className="text-xs text-red-400 mt-1">Failed</span>
              </div>
            ) : isLoadingThumbnail ? (
              <div className="absolute inset-0">
                <Skeleton className="w-full h-full rounded" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Spinner className="w-4 h-4 text-blue-500" />
                </div>
              </div>
            ) : (
              <div className={`w-full h-full transition-all duration-300 ${
                imageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
              }`}>
                {thumbnailUrl && (
                  <img
                    src={thumbnailUrl}
                    alt={group.prompt}
                    className="w-full h-full object-cover rounded-md"
                    onLoad={() => setImageLoaded(true)}
                  />
                )}
              </div>
            )}
          </button>
          
          {group.totalVersions > 1 && (
            <div className="absolute -top-0.5 -right-0.5">
              <button
                onClick={() => setShowVersions(!showVersions)}
                className="bg-gray-700 hover:bg-gray-600 px-1.5 py-0.5 rounded-full text-[10px] leading-none text-white transition-colors"
              >
                v{group.totalVersions}
              </button>
            </div>
          )}
          
          {showVersions && group.versions?.length > 0 && (
            <div className="absolute bottom-full mb-2 right-0 bg-gray-800 rounded-lg shadow-lg p-2 min-w-[150px] z-10">
              {group.versions.slice().reverse().map((version) => (
                <button
                  key={`version-${version.id}`}
                  onClick={() => selectImage({
                    ...group,
                    selectedVersion: version,
                    src: `${process.env.REACT_APP_API_URL}/generations/image/${version.id}`
                  })}
                  className="w-full text-left p-2 hover:bg-gray-700 rounded text-white text-sm"
                >
                  Version {version.versionNumber}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false)
          pendingImage.current = null
        }}
        onConfirm={() => {
          selectImage(pendingImage.current)
          setShowConfirmModal(false)
          pendingImage.current = null
        }}
        title="Unsaved Changes"
        message="You have unsaved brush changes. Are you sure you want to discard them and switch to a different image?"
        confirmButtonText="Discard Changes"
      />
    </div>
  )
}

export default React.memo(FilmstripItem)